import { useMemo } from "react";
import { useTheme } from "styled-components/native";
import {
  isCssLinearGradientString,
  resolveToLinearGradientConfig,
  LinearGradientConfig,
} from "@utils/themedBackground";

type GradientResult = Required<
  Pick<LinearGradientConfig, "colors" | "start" | "end" | "locations">
> & {
  isGradient: boolean;
};

/**
 * Hook to resolve a theme token to a gradient configuration.
 * Works with both solid colors and linear-gradient strings from theme tokens.
 *
 * @param token - Theme token key (e.g., "core-01", "action-primary-bg")
 * @param fallbackColor - Fallback color when token is missing or invalid
 * @returns Gradient configuration with colors, start, end, locations, and isGradient flag
 *
 * @example
 * // In a component:
 * const gradient = useThemedGradient("action-primary-bg", theme["core-01"]);
 *
 * // Use with LinearGradient:
 * <LinearGradient
 *   colors={gradient.colors}
 *   start={gradient.start}
 *   end={gradient.end}
 *   locations={gradient.locations}
 * />
 *
 * // Check if it's actually a gradient:
 * if (gradient.isGradient) {
 *   // render gradient
 * } else {
 *   // render solid color
 * }
 */
export const useThemedGradient = (
  token: string | undefined,
  fallbackColor: string = "transparent"
): GradientResult => {
  const theme = useTheme();

  return useMemo(() => {
    let rawValue: unknown;

    if (token != null) {
      if (isCssLinearGradientString(token)) {
        rawValue = token;
      } else {
        rawValue = (theme as Record<string, unknown>)?.[token];
      }
    }

    const resolvedValue = rawValue ?? fallbackColor;
    const isGradient = isCssLinearGradientString(resolvedValue);
    const gradient = resolveToLinearGradientConfig(
      resolvedValue,
      fallbackColor
    );

    return {
      ...gradient,
      isGradient,
    };
  }, [token, theme, fallbackColor]);
};

/**
 * Hook to get gradient config directly from a value (not a token).
 * Useful when you already have the value and don't need to look it up from theme.
 *
 * @param value - Direct value (color string or "linear-gradient(...)")
 * @param fallbackColor - Fallback color when value is invalid
 * @returns Gradient configuration
 */
export const useGradientFromValue = (
  value: unknown,
  fallbackColor: string = "transparent"
): GradientResult => {
  return useMemo(() => {
    const isGradient = isCssLinearGradientString(value);
    const gradient = resolveToLinearGradientConfig(value, fallbackColor);

    return {
      ...gradient,
      isGradient,
    };
  }, [value, fallbackColor]);
};

export default useThemedGradient;
